export interface HandlerResponseFailure {
  isBase64Encoded: boolean;
  statusCode: number;
  body: string;
}

export interface HandlerResponseSuccess {
  isBase64Encoded: boolean;
  statusCode: number;
  body: string;
  headers?: Record<string, string>;
}

export type HandlerResponse = HandlerResponseFailure | HandlerResponseSuccess;

export type NrtClassification =
  | "ALL"
  | "PRIMARY"
  | "FLEET"
  | "WHOLESALE"
  | "EXOTIC"
  | "CLASSIFIEDS";
export type NrtVehicleType = "ALL" | "NEW" | "USED";
export type NrtStatus = "ACTIVE" | "INACTIVE" | "TESTING";

export interface NrtRecord {
  accountId: string;
  id: string;
  caseNumber: string;
  // Comma-separated, single-quoted values (e.g. 'ALL','PRIMARY'); nullable so
  // reads tolerate legacy rows written before this field became required.
  classification: string | null;
  createdBy: string;
  dateCreated: string | null;
  expirationDate: string | null;
  lastAccessed: string | null;
  map: string | null;
  notes: string | null;
  providerId: string;
  select: string | null;
  status: NrtStatus;
  tags: string | null;
  type: NrtVehicleType | null;
}

export interface NrtPagination {
  pageSize: number;
  nextCursor: string | null;
}

// COMPLETE/ERROR match the check's own status vocabulary, per Rally's sample payloads.
export type NrtTestStatus = "IDLE" | "RUNNING" | "COMPLETE" | "ERROR";

// One matched record within a COMPLETE result's `items` list.
export interface NrtTestResultItem {
  vin: string;
  [key: string]: unknown;
}

// `timestamp`/`message` are null only for IDLE; `items` is non-empty iff status === "COMPLETE".
export interface NrtTestResult {
  status: NrtTestStatus;
  timestamp: string | null;
  message: string | null;
  items: NrtTestResultItem[];
}

// The never-run state. Mirrors buildInitialNrtRecordFormState so the modal
// seeds both form and test state the same way.
export const buildInitialNrtTestResult = (): NrtTestResult => ({
  status: "IDLE",
  timestamp: null,
  message: null,
  items: []
});

// The wire shape: getAllRecords casts DynamoDB attributes rather than
// converting, so the fetch layer must normalize before the UI sees them.
export type NrtRecordWire = Record<keyof NrtRecord, unknown>;

export interface NrtRecordsResponseBody {
  items: NrtRecordWire[];
  pagination: NrtPagination;
}

// Editable fields for the Add New Record form. `id`, `dateCreated` and
// `lastAccessed` are stamped by the backend, not entered by the user.
export type NrtRecordFormState = Omit<
  NrtRecord,
  "id" | "dateCreated" | "lastAccessed"
>;

// Every editable field except `tags` and `notes`, which are optional.
export interface NrtRecordFormValidationErrors {
  accountId?: string;
  caseNumber?: string;
  classification?: string;
  createdBy?: string;
  expirationDate?: string;
  map?: string;
  providerId?: string;
  select?: string;
  status?: string;
  type?: string;
}

export const NrtRecordValidationConstraints = {
  ACCOUNT_ID_REQUIRED: "Account ID is required",
  CASE_NUMBER_REQUIRED: "Case number is required",
  CLASSIFICATION_REQUIRED: "Classification is required",
  CREATED_BY_REQUIRED: "Created by is required",
  EXPIRATION_DATE_REQUIRED: "Expiration date is required",
  EXPIRATION_DATE_IN_PAST:
    "Expiration date cannot be in the past — choose today or a later date",
  MAP_REQUIRED: "Map is required",
  PROVIDER_ID_REQUIRED: "Provider ID is required",
  SELECT_REQUIRED: "Select is required",
  STATUS_REQUIRED: "Status is required",
  TYPE_REQUIRED: "Type is required"
};

export const buildInitialNrtRecordFormState = (
  initialValues?: NrtRecordFormState
): NrtRecordFormState => ({
  accountId: initialValues?.accountId ?? "",
  caseNumber: initialValues?.caseNumber ?? "",
  classification: initialValues?.classification ?? "ALL",
  createdBy: initialValues?.createdBy ?? "",
  expirationDate: initialValues?.expirationDate ?? "",
  map: initialValues?.map ?? "",
  notes: initialValues?.notes ?? "",
  providerId: initialValues?.providerId ?? "",
  select: initialValues?.select ?? "",
  status: initialValues?.status ?? "ACTIVE",
  tags: initialValues?.tags ?? "",
  type: initialValues?.type ?? "ALL"
});

// Null-safe: several form fields are `string | null`, so `.trim()` alone throws.
const isBlank = (value: string | null | undefined): boolean =>
  !value || !value.trim();

// Use UTC date to match toUnixTimestampString's UTC midnight parsing.
const todayDateInputValue = (): string => {
  const today = new Date();
  const year = today.getUTCFullYear();
  const month = String(today.getUTCMonth() + 1).padStart(2, "0");
  const date = String(today.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${date}`;
};

export const validateNrtRecordForm = (
  formState: NrtRecordFormState
): NrtRecordFormValidationErrors | null => {
  const errors: NrtRecordFormValidationErrors = {};

  if (isBlank(formState.accountId)) {
    errors.accountId = NrtRecordValidationConstraints.ACCOUNT_ID_REQUIRED;
  }
  if (isBlank(formState.caseNumber)) {
    errors.caseNumber = NrtRecordValidationConstraints.CASE_NUMBER_REQUIRED;
  }
  if (isBlank(formState.createdBy)) {
    errors.createdBy = NrtRecordValidationConstraints.CREATED_BY_REQUIRED;
  }
  if (isBlank(formState.providerId)) {
    errors.providerId = NrtRecordValidationConstraints.PROVIDER_ID_REQUIRED;
  }
  if (isBlank(formState.expirationDate)) {
    errors.expirationDate =
      NrtRecordValidationConstraints.EXPIRATION_DATE_REQUIRED;
  } else if ((formState.expirationDate as string) < todayDateInputValue()) {
    errors.expirationDate =
      NrtRecordValidationConstraints.EXPIRATION_DATE_IN_PAST;
  }
  if (isBlank(formState.select)) {
    errors.select = NrtRecordValidationConstraints.SELECT_REQUIRED;
  }
  if (isBlank(formState.map)) {
    errors.map = NrtRecordValidationConstraints.MAP_REQUIRED;
  }
  // Dropdowns: classification is multi-select and can be cleared to empty.
  if (isBlank(formState.classification)) {
    errors.classification =
      NrtRecordValidationConstraints.CLASSIFICATION_REQUIRED;
  }
  if (isBlank(formState.type)) {
    errors.type = NrtRecordValidationConstraints.TYPE_REQUIRED;
  }
  if (isBlank(formState.status)) {
    errors.status = NrtRecordValidationConstraints.STATUS_REQUIRED;
  }

  // `tags` and `notes` are deliberately absent — both are optional.
  return Object.keys(errors).length > 0 ? errors : null;
};
export interface CreateNrtRecordRequest {
  accountId: string;
  caseNumber: string;
  // A single NrtClassification value, or a comma-separated list of
  // single-quoted values (e.g. 'ALL','PRIMARY') for multi-select.
  classification: string;
  createdBy: string;
  expirationDate: string;
  map: string;
  notes?: string | null;
  providerId: string;
  select: string;
  status: NrtStatus;
  tags?: string | null;
  type: NrtVehicleType;
}

export interface UpdateNrtRecordRequest {
  accountId: string;
  id: string;
  caseNumber?: string;
  classification?: string;
  expirationDate?: string;
  map?: string;
  notes?: string | null;
  providerId?: string;
  select?: string;
  status?: NrtStatus;
  tags?: string | null;
  type?: NrtVehicleType;
}

// Single source of truth for the Lambda's HTTP routes, mirrored manually in
// sls.yml (YAML can't import this) and used by the dispatcher's matching.
export const NrtDatabaseFilterToolLambdaRoutes = {
  GET_ALL_RECORDS: {
    method: "GET",
    path: "/nrtDatabaseFilterTool/getAllRecords"
  },
  CREATE_RECORD: {
    method: "POST",
    path: "/nrtDatabaseFilterTool/createRecord"
  },
  UPDATE_RECORD: {
    method: "PUT",
    path: "/nrtDatabaseFilterTool/updateRecord"
  }
} as const;
